<script lang="ts" setup>
import type { CarsListCar, SearchListCar } from '@autobid/ui/types/Car'
import CarHoverDetails from '@autobid/ui/components/elements/car/CarHoverDetails.vue'
import FinishedImageOverlay from '@autobid/ui/components/elements/car/FinishedImageOverlay.vue'
import { useCurrentAuctionState } from '@autobid/ui/composables/car/useCurrentAuctionState'
import { useAuctionPagesLink } from '@autobid/ui/composables/useAuctionPagesLink'
import { useIsMobile } from '@autobid/ui/composables/useIsMobile'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed, onMounted, ref } from 'vue'
import 'swiper/css'
import 'swiper/css/pagination'

interface Props {
  car: CarsListCar | SearchListCar
}

const props = defineProps<Props>()
const imageLoaded = ref<boolean | undefined>()
const images = props.car.imageGroups.itemMainImageSubgroup
const state = useCurrentAuctionState(computed(() => props.car))
const isMobile = useIsMobile()
const carHoverDetails = ref()
const { getLink } = useAuctionPagesLink(props.car.appId)
const maxSliderImageIndex = ref(0)

const barometerUrl = computed(() =>
  getLink({
    page: 'barometer',
    slug: props.car.slug,
  }),
)

const THUMBNAIL_SIZES = {
  m: 430,
  l: 1279,
}

function showPreview(img?: string) {
  carHoverDetails.value?.showPreview(img)
}

function hidePreview() {
  carHoverDetails.value?.hidePreview()
}

function handleSlideChange({ activeIndex }: { activeIndex: number }) {
  maxSliderImageIndex.value = Math.max(activeIndex, maxSliderImageIndex.value)
}

function initImageLoadedState() {
  imageLoaded.value = process.server
}
initImageLoadedState()

onMounted(() => {
  setTimeout(() => {
    imageLoaded.value = true
  }, 500)
})
</script>

<template>
  <div class="relative w-1/2 min-w-[50%] md:w-full md:min-w-full">
    <div
      v-if="car.stage !== 'REMOVED' && car.isHighestBidder"
      class="absolute left-2 top-2 z-30"
      @click.stop
    >
      <ElementsCarHighestBidderTooltip />
    </div>

    <div
      class="relative z-10 mb-3 w-full"
      :class="{ 'hidden md:block': car.stage !== 'REMOVED' }"
    >
      <div class="relative z-10 flex w-full overflow-hidden">
        <DynamicAuctionItemListCarThumbnailCarRemoved
          v-if="car.stage === 'REMOVED'"
        />

        <common-link
          v-else
          :href="barometerUrl"
          target="_self"
          class="flex w-full min-w-full items-center justify-center"
          :class="{
            'bg-black': images.length,
          }"
          @click.prevent
          @mouseenter="showPreview(images[0]?.links.l)"
          @mouseleave="hidePreview"
        >
          <CommonPicture
            v-if="images.length"
            :images="images[0].links"
            :sizes="THUMBNAIL_SIZES"
            default-size="m"
            class="flex h-auto w-full max-w-full object-contain md:max-h-[374px] lg:max-h-[370px] xl:max-h-[144px]"
            :alt="images[0].description.categoryLabel"
            loading="lazy"
            @load="imageLoaded = true"
          />
          <ElementsCarThumbnailPlaceholder v-else />
        </common-link>
      </div>
      <FinishedImageOverlay v-if="imageLoaded" :car="car" :state="state" />
    </div>

    <div
      v-if="car.stage !== 'REMOVED'"
      class="relative mb-2 w-full md:hidden"
      :class="{
        'after:absolute after:inset-0 after:z-0 after:bg-primary after:opacity-25':
          images.length,
      }"
    >
      <common-link
        :href="barometerUrl"
        target="_self"
        class="block"
        @click.stop
      >
        <client-only v-if="isMobile && images.length">
          <Swiper
            class="relative z-10 bg-black"
            pagination
            @slide-change="handleSlideChange"
          >
            <SwiperSlide
              v-for="(img, index) in images"
              :key="img.links.l"
              class="!flex items-center justify-center"
            >
              <CommonPicture
                v-if="index <= maxSliderImageIndex + 1"
                :images="img.links"
                :sizes="THUMBNAIL_SIZES"
                class="flex h-auto max-h-[227px] w-full max-w-full object-contain sm:max-h-[275px] [@media(max-width:400px)]:max-h-[137px]"
                default-size="l"
                :alt="img.description.categoryLabel"
                loading="lazy"
                @load="imageLoaded = true"
              />
            </SwiperSlide>
          </Swiper>
        </client-only>

        <ElementsCarThumbnailPlaceholder v-else />
      </common-link>

      <FinishedImageOverlay v-if="imageLoaded" :car="car" :state="state" />
    </div>

    <CarHoverDetails ref="carHoverDetails" :car="car" :images="images" />
  </div>
</template>

<style lang="scss" scoped>
.preview {
  @media (max-width: 1149px) {
    &-wrapper {
      display: none !important;
    }
  }

  @media (min-width: 1150px) {
    &-description {
      width: 200px;
      min-width: 200px;
    }

    &-img {
      width: 600px;
    }

    &-inner {
      height: 370px;
    }

    &-wrapper {
      margin-left: calc((100vw - 800px) / 2 - 30px);
    }
  }

  @media (min-width: 1440px) {
    &-wrapper {
      margin-left: 300px;
    }
  }
}
</style>
